<!--
  @component SecurityPanel — Phase 4 account-wide security posture + incidents.
  tier:   deployments section (scope-independent — posture is account/floor-level)

  The live security health of your account: authorization mode + role, the
  tamper-evident audit chain, the secrets vault, accrued governance debt, and
  the open incident set. All real floor reads (security.whoami / audit.verify /
  vault.status / debt.summary / incidents.list) — no fabrication. Pairs with
  Activity: Activity is the ledger, Security is the posture.
  reads: useDeploymentIO (security.* read surface)
-->
<script lang="ts">
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { relTime } from "../../home/home-util.js";

  const node = useDeploymentIO();

  interface Incident { id: string; title: string; type: string; severity: string; status: string; detectedAt: number; }
  interface VaultStatus { available: boolean; kekMode: string; sealed: boolean; secretCount: number; restored: boolean; }

  let role = $state<string | null>(null);
  let authzMode = $state<string | null>(null);
  let chainOk = $state<boolean | null>(null);
  let chainCount = $state<number | null>(null);
  let vault = $state<VaultStatus | null>(null);
  let debt = $state<{ totalScore: number; entryCount: number } | null>(null);
  let incidents = $state<Incident[]>([]);
  let loaded = $state(false);

  async function safe<T>(op: string): Promise<T | null> {
    try { return (await node.dispatch(op)) as T; } catch { return null; }
  }
  function coerceIncident(raw: unknown): Incident {
    const x = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
    return {
      id: typeof x["id"] === "string" ? x["id"] : "",
      title: typeof x["title"] === "string" ? x["title"] : "(untitled)",
      type: typeof x["type"] === "string" ? x["type"] : "",
      severity: typeof x["severity"] === "string" ? x["severity"] : "low",
      status: typeof x["status"] === "string" ? x["status"] : "open",
      detectedAt: typeof x["detectedAt"] === "number" ? x["detectedAt"] : 0,
    };
  }
  async function load(): Promise<void> {
    const [who, verify, vs, ds, inc] = await Promise.all([
      safe<{ role?: string; authzMode?: string }>("security.whoami"),
      safe<{ ok?: boolean; count?: number }>("security.audit.verify"),
      safe<VaultStatus>("security.vault.status"),
      safe<{ totalScore?: number; entryCount?: number }>("security.debt.summary"),
      safe<{ incidents?: unknown }>("security.incidents.list"),
    ]);
    role = who?.role ?? null;
    authzMode = who?.authzMode ?? null;
    chainOk = typeof verify?.ok === "boolean" ? verify.ok : null;
    chainCount = typeof verify?.count === "number" ? verify.count : null;
    vault = vs ?? null;
    debt = ds ? { totalScore: ds.totalScore ?? 0, entryCount: ds.entryCount ?? 0 } : null;
    incidents = Array.isArray(inc?.incidents) ? (inc.incidents as unknown[]).map(coerceIncident) : [];
    loaded = true;
  }
  $effect(() => { void load(); });

  const open = $derived(incidents.filter((i) => i.status !== "resolved" && i.status !== "closed"));

  function sevTone(sev: string): string {
    const s = sev.toLowerCase();
    if (s === "critical" || s === "high") return "error";
    if (s === "medium" || s === "moderate") return "warn";
    return "info";
  }
</script>

<div class="security">
  <header>
    <h1>Security</h1>
    <p class="lede">Your account's live security posture — authorization, the tamper-evident audit chain, the secrets vault, governance debt, and open incidents.</p>
  </header>

  {#if !loaded}
    <p class="muted">Loading posture…</p>
  {:else}
    <div class="cards">
      <section class="card" class:good={authzMode === "enforce"}>
        <span class="k">Authorization</span>
        <span class="v">{role ?? "—"}</span>
        <span class="sub">{authzMode ? `${authzMode} mode` : "mode unknown"}</span>
      </section>

      <section class="card" class:good={chainOk === true} class:bad={chainOk === false}>
        <span class="k">Audit chain</span>
        <span class="v">{chainOk === null ? "—" : chainOk ? "Intact" : "Tampered"}</span>
        <span class="sub">{chainCount !== null ? `${chainCount} entries · tamper-evident` : "verifyDurable"}</span>
      </section>

      <section class="card" class:good={vault?.available && !vault?.sealed} class:bad={vault?.sealed}>
        <span class="k">Secrets vault</span>
        <span class="v">{vault === null ? "—" : vault.sealed ? "Sealed" : vault.available ? "Unsealed" : "Unavailable"}</span>
        <span class="sub">{vault ? `${vault.secretCount} secret${vault.secretCount === 1 ? "" : "s"} · ${vault.kekMode}${vault.restored ? " · restored" : ""}` : "KEK-backed"}</span>
      </section>

      <section class="card" class:good={debt?.totalScore === 0} class:warn={!!debt && debt.totalScore > 0}>
        <span class="k">Governance debt</span>
        <span class="v">{debt === null ? "—" : debt.totalScore}</span>
        <span class="sub">{debt ? `${debt.entryCount} entr${debt.entryCount === 1 ? "y" : "ies"}` : "scan-accrued"}</span>
      </section>
    </div>

    <section class="incidents">
      <div class="inc-head">
        <h2>Incidents</h2>
        <span class="chip" class:clear={open.length === 0}>{open.length === 0 ? "All clear" : `${open.length} open`}</span>
      </div>
      {#if incidents.length === 0}
        <p class="muted">No incidents — the detect floor hasn't raised anything.</p>
      {:else}
        <ul class="inc-list">
          {#each incidents as i (i.id)}
            <li class="inc" class:resolved={i.status === "resolved" || i.status === "closed"}>
              <span class="sev {sevTone(i.severity)}" title={i.severity}></span>
              <div class="inc-main">
                <span class="inc-title">{i.title}</span>
                <span class="inc-meta">{i.type || "incident"} · {i.severity}{i.detectedAt ? ` · ${relTime(i.detectedAt)}` : ""}</span>
              </div>
              <span class="inc-status {i.status}">{i.status}</span>
            </li>
          {/each}
        </ul>
      {/if}
    </section>
  {/if}
</div>

<style>
  .security { display: flex; flex-direction: column; gap: var(--space-4); max-width: 1100px; font-family: var(--type-font); }
  header { display: flex; flex-direction: column; gap: var(--space-1); }
  h1 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .lede { margin: 0; font-size: 13.5px; color: var(--text-secondary); line-height: 1.55; max-width: 660px; }
  .muted { font-size: 13px; color: var(--text-tertiary); }

  .cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-3); }
  @media (max-width: 860px) { .cards { grid-template-columns: repeat(2, 1fr); } }
  .card { display: flex; flex-direction: column; gap: 4px; padding: var(--space-4) var(--space-4) var(--space-3); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-lg, 14px); }
  .card .k { font-size: 11px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-tertiary); }
  .card .v { font-size: 22px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.1; color: var(--text-primary); }
  .card .sub { font-size: 11.5px; color: var(--text-tertiary); }
  .card.good .v { color: var(--success); }
  .card.warn .v { color: var(--warning); }
  .card.bad .v { color: var(--error); }
  .card.good { border-color: color-mix(in srgb, var(--success) 28%, var(--border)); }
  .card.bad { border-color: color-mix(in srgb, var(--error) 32%, var(--border)); }

  .incidents { display: flex; flex-direction: column; gap: var(--space-3); }
  .inc-head { display: flex; align-items: center; gap: var(--space-3); }
  h2 { margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary); }
  .chip { font-size: 11px; font-weight: 500; padding: 2px var(--space-2); border-radius: var(--radius-pill); background: color-mix(in srgb, var(--warning) 16%, transparent); color: var(--warning); }
  .chip.clear { background: color-mix(in srgb, var(--success) 14%, transparent); color: var(--success); }
  .inc-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-2); }
  .inc { display: flex; align-items: center; gap: var(--space-3); padding: var(--space-3); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-md); }
  .inc.resolved { opacity: 0.6; }
  .sev { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: var(--accent, #5b8cff); }
  .sev.error { background: var(--error); } .sev.warn { background: var(--warning); } .sev.info { background: var(--accent, #5b8cff); }
  .inc-main { display: flex; flex-direction: column; min-width: 0; flex: 1; }
  .inc-title { font-size: 13.5px; font-weight: 500; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .inc-meta { font-size: 11.5px; color: var(--text-tertiary); font-family: var(--type-mono); }
  .inc-status { flex-shrink: 0; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 8px; border-radius: var(--radius-pill); background: var(--bg-hover); color: var(--text-secondary); }
  .inc-status.resolved, .inc-status.closed { color: var(--success); background: color-mix(in srgb, var(--success) 14%, transparent); }
</style>
